import { useEffect, useRef, useState } from "react";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Info, Check, MapPin, Building2 } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";

interface VenueAddressFieldProps {
  form: any;
  fieldPrefix?: string;
}

interface PlaceSuggestion {
  placeId: string;
  mainText: string;
  secondaryText: string;
  fullText: string;
  types: string[];
}

const VenueAddressField = ({ form, fieldPrefix = "" }: VenueAddressFieldProps) => {
  const venueAddressField = fieldPrefix ? `${fieldPrefix}.venueAddress` : "venueAddress";
  const venueAddressVerifiedField = fieldPrefix ? `${fieldPrefix}.venueAddressVerified` : "venueAddressVerified";
  const venueCoordinatesField = fieldPrefix ? `${fieldPrefix}.venueCoordinates` : "venueCoordinates";
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [isVerified, setIsVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const sessionTokenRef = useRef<string>(crypto.randomUUID());
  
  // Watch for saved venue data
  const savedAddress = form.watch(venueAddressField as any);
  const savedVerified = form.watch(venueAddressVerifiedField as any);
  const savedCoords = form.watch(venueCoordinatesField as any);

  // Load saved venue data
  useEffect(() => {
    if (!savedAddress || !savedVerified || !savedCoords) return;
    if (inputValue !== savedAddress) {
      setInputValue(savedAddress);
      setIsVerified(true);
    }
  }, [savedAddress, savedVerified, savedCoords, inputValue]);

  const handleAddressInput = (value: string) => {
    setInputValue(value);
    setIsVerified(false);
    form.setValue(venueAddressVerifiedField as any, false);
    
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    if (value.length > 2) {
      debounceTimer.current = setTimeout(async () => {
        setIsLoading(true);
        try {
          const { data, error } = await supabase.functions.invoke('google-places-autocomplete', {
            body: { 
              input: value,
              sessionToken: sessionTokenRef.current,
            },
            headers: {
              'x-session-id': sessionStorage.getItem('session_id') || 'unknown',
            },
          });

          if (error) {
            console.error("Error fetching suggestions:", error);
            return;
          }

          if (data?.suggestions) {
            setSuggestions(data.suggestions);
            setShowSuggestions(true);
          }
        } catch (error) {
          console.error("Error fetching suggestions:", error);
        } finally {
          setIsLoading(false);
        }
      }, 300);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = async (suggestion: PlaceSuggestion) => {
    setShowSuggestions(false);
    setSuggestions([]);
    setIsLoading(true);
    
    try {
      // Fetch place details to get coordinates
      const { data, error } = await supabase.functions.invoke('google-places-detail', {
        body: { 
          placeId: suggestion.placeId,
          sessionToken: sessionTokenRef.current,
        },
      });

      // Generate a new session token after place selection (Google billing best practice)
      sessionTokenRef.current = crypto.randomUUID();

      if (error || !data?.lat || !data?.lng) {
        console.error("Error fetching place details:", error);
        // Still set the address text even if coordinates fail
        const displayAddress = suggestion.fullText || `${suggestion.mainText}, ${suggestion.secondaryText}`;
        form.setValue(venueAddressField as any, displayAddress, { shouldValidate: true });
        setInputValue(displayAddress);
        return;
      }

      const displayAddress = data.displayName 
        ? `${data.displayName}, ${data.formattedAddress}`
        : data.formattedAddress;

      form.setValue(venueAddressField as any, displayAddress, { shouldValidate: true });
      form.setValue(venueAddressVerifiedField as any, true);
      form.setValue(venueCoordinatesField as any, { lng: data.lng, lat: data.lat });
      setInputValue(displayAddress);
      setIsVerified(true);

      // Mark the event as unsaved if this is part of an event
      if (fieldPrefix && fieldPrefix.includes("eventDates")) {
        const match = fieldPrefix.match(/eventDates\.(\d+)/);
        if (match) {
          const eventIndex = parseInt(match[1]);
          const currentDates = form.getValues("eventDates") || [];
          if (currentDates[eventIndex]) {
            currentDates[eventIndex].saved = false;
            form.setValue("eventDates", currentDates);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching place details:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormField
      control={form.control}
      name={venueAddressField as any}
      render={({ field, fieldState }) => (
        <FormItem>
          <div className="flex items-center gap-2 mb-1">
            <FormLabel className={(inputValue && !isVerified) ? "text-destructive" : ""}>Venue Address</FormLabel>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs">Search by venue name or address. This is flexible and can be updated later.</p>
              </TooltipContent>
            </Tooltip>
            {isVerified && savedCoords && (
              <Badge 
                variant="secondary" 
                className="relative gap-1 py-0.5 px-2 text-[10px] font-bold tracking-tight bg-gradient-to-r from-green-500/25 via-green-400/25 to-green-500/25 text-green-600 dark:text-green-400 border border-green-500/60 shadow-md shadow-green-500/25 rounded overflow-hidden before:absolute before:inset-0 before:bg-gradient-to-r before:from-transparent before:via-white/25 before:to-transparent before:animate-[shimmer_2s_ease-in-out_infinite] animate-fade-in"
              >
                <Check className="h-2.5 w-2.5 relative z-10 drop-shadow-[0_0_6px_rgba(34,197,94,1)] stroke-[3]" />
                <span className="relative z-10">Verified</span>
              </Badge>
            )}
          </div>
          <div className="relative">
            <FormControl>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search venue name or address..."
                  value={inputValue}
                  onChange={(e) => {
                    handleAddressInput(e.target.value);
                    field.onChange(e.target.value);
                  }}
                  className={`bg-background/50 pl-9 ${(fieldState.error && !isVerified) ? "border-destructive" : ""}`}
                />
                {isLoading && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="h-4 w-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                  </div>
                )}
              </div>
            </FormControl>
            
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-lg max-h-64 overflow-auto">
                {suggestions.map((suggestion, index) => (
                  <button
                    key={suggestion.placeId || index}
                    type="button"
                    className="w-full px-3 py-2.5 text-left hover:bg-accent hover:text-accent-foreground transition-colors flex items-start gap-3"
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    <Building2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{suggestion.mainText}</div>
                      <div className="text-xs text-muted-foreground truncate">{suggestion.secondaryText}</div>
                    </div>
                  </button>
                ))}
                <div className="px-3 py-1.5 text-[10px] text-muted-foreground/50 border-t flex items-center gap-1">
                  Powered by Google
                </div>
              </div>
            )}
          </div>

          {form.formState.errors.venueAddress && form.formState.isSubmitted && !isVerified && (
            <p className="text-sm font-medium text-destructive mt-1">
              {form.formState.errors.venueAddress.message as string}
            </p>
          )}
        </FormItem>
      )}
    />
  );
};

export default VenueAddressField;
